import { axiosInstance } from './api';
import type { Paginated } from './types';

export interface ZoneRemindersSettingsDto {
  id: number;
  zone: string;
  /** Per-zone Чеклисты feature flag — mirrors visits' VisitsZoneSettings.enabled.
   *  Off = the zone shows only an "Включить" affordance; on = the Чеклисты tab
   *  appears in the sidebar/Live/Statistics (Alexey 2026-08-24). */
  checklists_enabled: boolean;
  deadline_pre_reminder_minutes: number;
  overdue_minutes: number;
  send_pre_deadline_message: boolean;
  send_deadline_message: boolean;
  send_overdue_message: boolean;
  ops_tasks_chat_id: string | null;
  ops_tasks_thread_id: string | null;
}

export interface ChecklistItemDto {
  id: number;
  schedule_type: 'weekly' | 'monthly' | 'once';
  weekdays_mask: number;
  month_day: number | null;
  specific_date: string | null;
  due_time: string;
  task_text: string;
  answer_format: string;
  active: boolean;
}

export interface ChecklistDto {
  id: number;
  zone: string;
  sheet_url: string;
  worksheet_title: string;
  sync_on: boolean;
  last_synced_at: string | null;
  last_sync_error: string | null;
  retry_scheduled_until: string | null;
  /** Latest sync progress (Alexey #5911): pending = queued by the button,
   *  running = reading the sheet, ok / error = finished (text in last_sync_error). */
  sync_state: 'pending' | 'running' | 'ok' | 'error' | null;
  sync_started_at: string | null;
  sync_finished_at: string | null;
  sync_items_count: number | null;
  items: ChecklistItemDto[];
}

export const getRemindersSettings = (zoneId: string) =>
  axiosInstance.get<ZoneRemindersSettingsDto>(`/ops_tasks/reminders-settings/${zoneId}/`);

export const patchRemindersSettings = (zoneId: string, data: Partial<Omit<ZoneRemindersSettingsDto, 'id' | 'zone'>>) =>
  axiosInstance.patch<ZoneRemindersSettingsDto>(`/ops_tasks/reminders-settings/${zoneId}/`, data);

export const listChecklists = (zoneId: string, pageSize = 100) =>
  axiosInstance.get<Paginated<ChecklistDto>>(`/ops_tasks/api/checklists/?zone_id=${zoneId}&page_size=${pageSize}`);

export const createChecklist = (data: { zone: string; sheet_url: string; worksheet_title?: string; sync_on?: boolean }) =>
  axiosInstance.post<ChecklistDto>('/ops_tasks/api/checklists/', data);

export const patchChecklist = (id: number, data: Partial<Omit<ChecklistDto, 'id' | 'zone' | 'last_synced_at' | 'retry_scheduled_until' | 'sync_state' | 'sync_started_at' | 'sync_finished_at' | 'sync_items_count'>>) =>
  axiosInstance.patch<ChecklistDto>(`/ops_tasks/api/checklists/${id}/`, data);

export const deleteChecklist = (id: number) =>
  axiosInstance.delete(`/ops_tasks/api/checklists/${id}/`);

export const syncChecklist = (id: number) =>
  axiosInstance.post<ChecklistDto>(`/ops_tasks/api/checklists/${id}/sync/`);

export const getChecklist = (id: number) =>
  axiosInstance.get<ChecklistDto>(`/ops_tasks/api/checklists/${id}/`);

export const initRemindersSettings = (zoneId: string) =>
  axiosInstance.post<ZoneRemindersSettingsDto>(`/ops_tasks/reminders-settings/${zoneId}/`);

export type ChecklistItemPayload = Partial<Omit<ChecklistItemDto, 'id'>>;

export const createManualChecklist = (data: { zone: string; worksheet_title?: string }) =>
  axiosInstance.post<ChecklistDto>('/ops_tasks/api/checklists/manual/', data);

/** Copy the standard Google Sheets template for the zone («Чеклист <zone>»,
 *  first sheet «Рабочий чеклист») and register a synced checklist on it. */
export const createChecklistFromTemplate = (data: { zone: string }) =>
  axiosInstance.post<ChecklistDto>('/ops_tasks/api/checklists/from-template/', data);

export const createChecklistItem = (checklistId: number, data: ChecklistItemPayload) =>
  axiosInstance.post<ChecklistItemDto>(`/ops_tasks/api/checklists/${checklistId}/items/`, data);

export const updateChecklistItem = (checklistId: number, itemId: number, data: ChecklistItemPayload) =>
  axiosInstance.patch<ChecklistItemDto>(`/ops_tasks/api/checklists/${checklistId}/items/${itemId}/`, data);

export const deleteChecklistItem = (checklistId: number, itemId: number) =>
  axiosInstance.delete(`/ops_tasks/api/checklists/${checklistId}/items/${itemId}/`);
